import { axiosPrivate } from '../../api/axios';
import { getSocket } from '../../services/socket';
import { leaveVideoSession } from '../../hooks/useVideoSession';
import {
    setZoomLoading,
    setZoomSlotsData,
    setJoinCredentials,
    setRecording,
    setZoomError,
    leaveMeeting,
} from '../reducers/zoom';

const applySlotsPayload = (dispatch, data) => {
    dispatch(setZoomSlotsData({
        slots: data.slots,
        currentLiveSession: data.currentLiveSession,
        latestPastSession: data.latestPastSession,
        zoomConfigured: data.zoomConfigured,
        zoomEnabled: data.zoomEnabled,
    }));
};

export const fetchCourseSlots = (courseId) => async (dispatch) => {
    dispatch(setZoomLoading(true));
    dispatch(setZoomError(null));
    try {
        const { data } = await axiosPrivate.get(`/api/zoom/courses/${courseId}/classes`);
        dispatch(setZoomSlotsData(data));
        return data;
    } catch (error) {
        dispatch(setZoomError(error.response?.data?.message || 'Error al cargar clases'));
        return null;
    } finally {
        dispatch(setZoomLoading(false));
    }
};

export const createManualClass = (courseId, payload) => async (dispatch) => {
    dispatch(setZoomError(null));
    try {
        const { data } = await axiosPrivate.post(
            `/api/zoom/courses/${courseId}/classes`,
            payload,
        );
        applySlotsPayload(dispatch, data);
        return data.session;
    } catch (error) {
        dispatch(setZoomError(error.response?.data?.message || 'Error al crear la clase'));
        throw error;
    }
};

export const updateClassExternalLink = (sessionId, externalLink) => async (dispatch) => {
    try {
        const { data } = await axiosPrivate.patch(
            `/api/zoom/sessions/${sessionId}/external-link`,
            { externalLink },
        );
        applySlotsPayload(dispatch, data);
        return data.session;
    } catch (error) {
        const message = error.response?.data?.message || 'Error al guardar el link';
        const err = new Error(message);
        err.response = error.response;
        throw err;
    }
};

export const createClassSession = (courseId, slotKey, title) => async (dispatch) => {
    dispatch(setZoomLoading(true));
    dispatch(setZoomError(null));
    try {
        const { data } = await axiosPrivate.post(
            `/api/zoom/courses/${courseId}/slots/${encodeURIComponent(slotKey)}/create`,
            { title },
        );
        applySlotsPayload(dispatch, data);
        return data.session;
    } catch (error) {
        dispatch(setZoomError(error.response?.data?.message || 'Error al activar la clase'));
        throw error;
    } finally {
        dispatch(setZoomLoading(false));
    }
};

export const createTestClassSession = (courseId, title) => async (dispatch) => {
    dispatch(setZoomLoading(true));
    dispatch(setZoomError(null));
    try {
        const { data } = await axiosPrivate.post(
            `/api/zoom/courses/${courseId}/sessions/test`,
            { title },
        );
        applySlotsPayload(dispatch, data);
        return data.session;
    } catch (error) {
        dispatch(setZoomError(error.response?.data?.message || 'Error al crear la sesión'));
        throw error;
    } finally {
        dispatch(setZoomLoading(false));
    }
};

export const endTestClassSession = (sessionId) => async (dispatch, getState) => {
    dispatch(setZoomLoading(true));
    dispatch(setZoomError(null));
    try {
        const { data } = await axiosPrivate.post(`/api/zoom/sessions/${sessionId}/end-test`);
        const { joinCredentials } = getState().zoom;
        if (String(joinCredentials?.session?._id) === String(sessionId)) {
            await leaveVideoSession({ notify: false });
            dispatch(leaveMeeting());
        }
        applySlotsPayload(dispatch, data);
        return data;
    } catch (error) {
        dispatch(setZoomError(error.response?.data?.message || 'Error al terminar sesión de prueba'));
        throw error;
    } finally {
        dispatch(setZoomLoading(false));
    }
};

/** Termina solo la videollamada Zoom. La clase sigue abierta. */
export const endClassSession = (sessionId) => async (dispatch, getState) => {
    dispatch(setZoomLoading(true));
    dispatch(setZoomError(null));
    try {
        const { data } = await axiosPrivate.post(`/api/zoom/sessions/${sessionId}/end`);
        const { joinCredentials } = getState().zoom;
        if (String(joinCredentials?.session?._id) === String(sessionId)) {
            await leaveVideoSession({ notify: false });
            dispatch(leaveMeeting());
        }
        applySlotsPayload(dispatch, data);
        return data;
    } catch (error) {
        dispatch(setZoomError(error.response?.data?.message || 'Error al terminar la videollamada'));
        throw error;
    } finally {
        dispatch(setZoomLoading(false));
    }
};

/** Cierra la clase y, si hay Zoom en vivo, también termina la llamada. */
export const closeClassSession = (sessionId) => async (dispatch, getState) => {
    dispatch(setZoomLoading(true));
    dispatch(setZoomError(null));
    try {
        const { data } = await axiosPrivate.post(`/api/zoom/sessions/${sessionId}/close`);
        const { joinCredentials } = getState().zoom;
        if (String(joinCredentials?.session?._id) === String(sessionId)) {
            await leaveVideoSession({ notify: false });
            dispatch(leaveMeeting());
        }
        applySlotsPayload(dispatch, data);
        return data;
    } catch (error) {
        dispatch(setZoomError(error.response?.data?.message || 'Error al terminar la clase'));
        throw error;
    } finally {
        dispatch(setZoomLoading(false));
    }
};

export const joinClassSession = (sessionId) => async (dispatch) => {
    dispatch(setZoomError(null));
    try {
        const { data } = await axiosPrivate.post(`/api/zoom/sessions/${sessionId}/join`, {
            socketId: getSocket()?.id || undefined,
        });
        dispatch(setJoinCredentials(data));
        return data;
    } catch (error) {
        dispatch(setZoomError(error.response?.data?.message || 'Error al unirse a la clase'));
        throw error;
    }
};

export const fetchSessionRecording = (sessionId, recordingId = null) => async (dispatch, getState) => {
    dispatch(setZoomLoading(true));
    dispatch(setZoomError(null));
    try {
        const { data } = await axiosPrivate.get(`/api/zoom/sessions/${sessionId}/recording`, {
            params: recordingId ? { recordingId } : undefined,
        });
        const { joinCredentials, inMeeting, videoMode } = getState().zoom;
        if (joinCredentials || inMeeting || videoMode === 'live') {
            return null;
        }
        dispatch(setRecording({
            playUrl: data.recording.playUrl,
            sessionId,
            recordingId: data.recording._id || recordingId,
        }));
        return data.recording;
    } catch (error) {
        dispatch(setZoomError(error.response?.data?.message || 'Grabación no disponible'));
        return null;
    } finally {
        dispatch(setZoomLoading(false));
    }
};

/**
 * Consulta Zoom en silencio (sin toast/loading) por si la grabación ya terminó de procesarse.
 * Si está lista, refresca los slots para que aparezca «Ver clase».
 */
export const pollSessionRecordingReady = (sessionId, courseId) => async (dispatch) => {
    if (!sessionId || !courseId) return false;
    try {
        const { data } = await axiosPrivate.get(`/api/zoom/sessions/${sessionId}/recording`);
        if (!data?.recording?.playUrl) return false;

        const slotsRes = await axiosPrivate.get(`/api/zoom/courses/${courseId}/classes`);
        dispatch(setZoomSlotsData(slotsRes.data));
        return true;
    } catch {
        return false;
    }
};

/** Vincula el estado de grabación Zoom a la ClassSession (programada o de prueba). */
export const markSessionRecording = (sessionId, pending = true, zoomCallId = null) => async (dispatch) => {
    const { data } = await axiosPrivate.post(
        `/api/zoom/sessions/${sessionId}/recording/state`,
        { pending, zoomCallId },
    );
    if (data?.slots) {
        applySlotsPayload(dispatch, data);
    }
    return data?.session || null;
};
